import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3,
  Check,
  ChevronRight,
  LogOut,
  Plus,
  Save,
  Search,
  Shield,
  Trash2,
  Users,
} from 'lucide-react';
import {
  createUser,
  getAdminStatus,
  getMe,
  listChannelMappings,
  listUsers,
  listViewerGroups,
  saveChannelMapping,
  saveViewerGroup,
  searchScopeOptions,
  updateUser,
  type ChannelMappingDraft,
  type UserDraft,
  type ViewerGroupDraft,
} from '../api.js';
import { logout } from '../auth.js';
import BrandLogo from '../components/BrandLogo.js';
import type { ChannelMapping, PortalUser, ScopeOption, ScopeRule, ScopeType, ViewerGroup } from '../types.js';

const emptyGroup: ViewerGroupDraft = {
  code: '',
  name: '',
  description: '',
  logoUrl: '',
  showRevenue: false,
  active: true,
  locale: 'de-DE',
  scopeRules: [],
};

const emptyUser: UserDraft = {
  email: '',
  displayName: '',
  viewerGroupId: null,
  role: 'viewer',
  disabled: false,
  oidcSubject: '',
  password: '',
};

const scopeLabels: Record<ScopeType, string> = {
  category_level1: 'Warengruppe',
  article_category: 'Kategorie',
  article: 'Artikel-ID',
  article_number: 'Artikelnummer',
};

export default function AdminPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'groups' | 'users' | 'channels'>('groups');
  const me = useQuery({ queryKey: ['me'], queryFn: getMe });
  const groups = useQuery({ queryKey: ['viewer-groups'], queryFn: listViewerGroups });
  const users = useQuery({ queryKey: ['users'], queryFn: listUsers });
  const channelMappings = useQuery({ queryKey: ['channel-mappings'], queryFn: listChannelMappings });
  const authStatus = useQuery({ queryKey: ['auth-status'], queryFn: getAdminStatus });

  const [selectedGroupId, setSelectedGroupId] = useState<string | 'new'>('new');
  const selectedGroup = groups.data?.viewerGroups.find((group) => group.id === selectedGroupId);
  const groupDraft = useMemo(() => toGroupDraft(selectedGroup), [selectedGroup]);

  return (
    <main className="min-h-screen bg-[#f6f7f9] text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-3 px-5 py-4 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
          <div className="min-w-0 justify-self-start">
            <h1 className="text-xl font-semibold">Administration</h1>
          </div>
          <BrandLogo className="h-5 justify-self-center sm:h-6" />
          <div className="flex min-w-0 items-center justify-start gap-3 sm:justify-end">
            <span className="min-w-0 truncate text-sm text-slate-600">{me.data?.user.email}</span>
            <button
              type="button"
              title="Logout"
              onClick={logout}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
            >
              <LogOut size={17} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl gap-5 px-5 py-5 lg:grid-cols-[220px_1fr]">
        <nav className="flex gap-2 lg:flex-col">
          <TabButton active={tab === 'groups'} icon={<Shield size={17} />} onClick={() => setTab('groups')}>
            Viewer Groups
          </TabButton>
          <TabButton active={tab === 'users'} icon={<Users size={17} />} onClick={() => setTab('users')}>
            User
          </TabButton>
          <TabButton active={tab === 'channels'} icon={<BarChart3 size={17} />} onClick={() => setTab('channels')}>
            Channels
          </TabButton>
        </nav>

        {tab === 'groups' ? (
          <ViewerGroupsPanel
            isLoading={groups.isLoading}
            groups={groups.data?.viewerGroups ?? []}
            selectedGroupId={selectedGroupId}
            onSelect={setSelectedGroupId}
            initialDraft={groupDraft}
            onSaved={() => {
              void queryClient.invalidateQueries({ queryKey: ['viewer-groups'] });
              void queryClient.invalidateQueries({ queryKey: ['users'] });
            }}
          />
        ) : tab === 'users' ? (
          <UsersPanel
            users={users.data?.users ?? []}
            groups={groups.data?.viewerGroups ?? []}
            keycloakProvisioningEnabled={authStatus.data?.keycloakProvisioningEnabled ?? false}
            onSaved={() => {
              void queryClient.invalidateQueries({ queryKey: ['users'] });
            }}
          />
        ) : (
          <ChannelMappingsPanel
            mappings={channelMappings.data?.channelMappings ?? []}
            isLoading={channelMappings.isLoading}
            onSaved={() => {
              void queryClient.invalidateQueries({ queryKey: ['channel-mappings'] });
              void queryClient.invalidateQueries({ queryKey: ['dashboard-sales'] });
              void queryClient.invalidateQueries({ queryKey: ['article-detail'] });
            }}
          />
        )}
      </div>
    </main>
  );
}

function ViewerGroupsPanel({
  isLoading,
  groups,
  selectedGroupId,
  onSelect,
  initialDraft,
  onSaved,
}: {
  isLoading: boolean;
  groups: ViewerGroup[];
  selectedGroupId: string | 'new';
  onSelect: (id: string | 'new') => void;
  initialDraft: ViewerGroupDraft;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<ViewerGroupDraft>(initialDraft);
  const [scopeType, setScopeType] = useState<ScopeType>('category_level1');
  const [scopeQuery, setScopeQuery] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  const options = useQuery({
    queryKey: ['scope-options', scopeType, scopeQuery],
    queryFn: () => searchScopeOptions(scopeType, scopeQuery),
  });

  const mutation = useMutation({
    mutationFn: saveViewerGroup,
    onSuccess: (result) => {
      setFeedback('Gespeichert');
      onSelect(result.viewerGroup.id);
      onSaved();
    },
    onError: (error) => {
      setFeedback(error instanceof Error ? error.message : 'Speichern fehlgeschlagen');
    },
  });

  useEffect(() => setDraft(initialDraft), [initialDraft]);

  const codeIsValid = isViewerGroupCodeValid(draft.code);

  const addRule = (option: ScopeOption) => {
    const exists = draft.scopeRules.some(
      (rule) => rule.scope_type === option.type && rule.scope_value === option.value,
    );
    if (exists) return;
    setDraft({
      ...draft,
      scopeRules: [
        ...draft.scopeRules,
        {
          scope_type: option.type,
          scope_value: option.value,
          include_descendants: option.type === 'article_category' || option.type === 'category_level1',
          active: true,
        },
      ],
    });
  };

  return (
    <section className="grid gap-5 lg:grid-cols-[280px_1fr]">
      <aside className="rounded-md border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold">Viewer Groups</h2>
          <button
            type="button"
            title="Neue Viewer Group"
            onClick={() => onSelect('new')}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            <Plus size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="max-h-[calc(100vh-190px)] overflow-auto p-2">
          {isLoading ? <p className="px-2 py-3 text-sm text-slate-500">Lade...</p> : null}
          <button
            type="button"
            onClick={() => onSelect('new')}
            className={`mb-1 flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm ${
              selectedGroupId === 'new' ? 'bg-[#e5f6f7] text-[#0c6871]' : 'hover:bg-slate-50'
            }`}
          >
            Neu
            <ChevronRight size={15} aria-hidden="true" />
          </button>
          {groups.map((group) => (
            <button
              key={group.id}
              type="button"
              onClick={() => onSelect(group.id)}
              className={`mb-1 flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm ${
                selectedGroupId === group.id ? 'bg-[#e5f6f7] text-[#0c6871]' : 'hover:bg-slate-50'
              }`}
            >
              <span>
                <span className="block font-medium">{group.name}</span>
                <span className="block text-xs text-slate-500">{group.code}</span>
              </span>
              <ChevronRight size={15} aria-hidden="true" />
            </button>
          ))}
        </div>
      </aside>

      <section className="rounded-md border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <h2 className="text-sm font-semibold">{draft.id ? 'Viewer Group bearbeiten' : 'Viewer Group anlegen'}</h2>
          <div className="flex items-center gap-3">
            {feedback ? <span className="text-sm text-[#117f8a]">{feedback}</span> : null}
            <button
              type="button"
              title="Speichern"
              onClick={() => mutation.mutate(draft)}
              disabled={mutation.isPending || !codeIsValid || !draft.name}
              className="inline-flex items-center gap-2 rounded-md bg-[#117f8a] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0c6871] disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              <Save size={16} aria-hidden="true" />
              Speichern
            </button>
          </div>
        </div>

        <div className="grid gap-5 p-5 xl:grid-cols-[1fr_1.2fr]">
          <div className="space-y-4">
            <Field label="Code">
              <input
                value={draft.code}
                onChange={(event) => {
                  setFeedback(null);
                  setDraft({ ...draft, code: slugify(event.target.value) });
                }}
                className="w-full rounded-md border border-slate-300 px-3 py-2"
              />
              {!codeIsValid && draft.code ? (
                <span className="mt-1 block text-xs text-red-600">
                  Mindestens 3 Zeichen, nur Kleinbuchstaben, Zahlen und Bindestriche.
                </span>
              ) : null}
            </Field>
            <Field label="Name">
              <input
                value={draft.name}
                onChange={(event) => {
                  setFeedback(null);
                  setDraft({ ...draft, name: event.target.value });
                }}
                className="w-full rounded-md border border-slate-300 px-3 py-2"
              />
            </Field>
            <Field label="Beschreibung">
              <textarea
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                rows={3}
                className="w-full rounded-md border border-slate-300 px-3 py-2"
              />
            </Field>
            <div className="flex flex-wrap gap-4">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.active}
                  onChange={(event) => setDraft({ ...draft, active: event.target.checked })}
                />
                Aktiv
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.showRevenue}
                  onChange={(event) => setDraft({ ...draft, showRevenue: event.target.checked })}
                />
                Umsatz sichtbar
              </label>
            </div>
            <Field label="Sprache">
              <select
                value={draft.locale}
                onChange={(event) => setDraft({ ...draft, locale: event.target.value as 'de-DE' | 'en-US' })}
                className="w-full rounded-md border border-slate-300 px-3 py-2"
              >
                <option value="de-DE">Deutsch</option>
                <option value="en-US">English</option>
              </select>
            </Field>
          </div>

          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-[180px_1fr]">
              <select
                value={scopeType}
                onChange={(event) => setScopeType(event.target.value as ScopeType)}
                className="rounded-md border border-slate-300 px-3 py-2"
              >
                {Object.entries(scopeLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <label className="relative">
                <Search className="pointer-events-none absolute left-3 top-2.5 text-slate-400" size={17} />
                <input
                  value={scopeQuery}
                  onChange={(event) => setScopeQuery(event.target.value)}
                  placeholder="Suchen"
                  className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3"
                />
              </label>
            </div>

            <div className="max-h-44 overflow-auto rounded-md border border-slate-200">
              {(options.data?.options ?? []).map((option) => (
                <button
                  key={`${option.type}:${option.value}`}
                  type="button"
                  onClick={() => addRule(option)}
                  className="flex w-full items-center justify-between border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-slate-50"
                >
                  <span>
                    <span className="font-medium">{option.label}</span>
                    <span className="ml-2 text-xs text-slate-500">{option.value}</span>
                  </span>
                  <Plus size={15} aria-hidden="true" />
                </button>
              ))}
            </div>

            <div className="overflow-hidden rounded-md border border-slate-200">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Typ</th>
                    <th className="px-3 py-2">Wert</th>
                    <th className="px-3 py-2">Untergruppen</th>
                    <th className="w-12 px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {draft.scopeRules.map((rule, index) => (
                    <tr key={`${rule.scope_type}:${rule.scope_value}`} className="border-t border-slate-100">
                      <td className="px-3 py-2">{scopeLabels[rule.scope_type]}</td>
                      <td className="px-3 py-2 font-mono text-xs">{rule.scope_value}</td>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={rule.include_descendants}
                          onChange={(event) => updateRule(index, { include_descendants: event.target.checked })}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          title="Entfernen"
                          onClick={() =>
                            setDraft({
                              ...draft,
                              scopeRules: draft.scopeRules.filter((_, ruleIndex) => ruleIndex !== index),
                            })
                          }
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
                        >
                          <Trash2 size={15} aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </section>
  );

  function updateRule(index: number, patch: Partial<ScopeRule>) {
    setDraft({
      ...draft,
      scopeRules: draft.scopeRules.map((rule, ruleIndex) =>
        ruleIndex === index ? { ...rule, ...patch } : rule,
      ),
    });
  }
}

function UsersPanel({
  users,
  groups,
  keycloakProvisioningEnabled,
  onSaved,
}: {
  users: PortalUser[];
  groups: ViewerGroup[];
  keycloakProvisioningEnabled: boolean;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<UserDraft>(emptyUser);
  const [feedback, setFeedback] = useState<string | null>(null);
  const createMutation = useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      setDraft(emptyUser);
      setFeedback('User angelegt');
      onSaved();
    },
    onError: (error) => {
      setFeedback(error instanceof Error ? `Fehler: ${error.message}` : 'Fehler: User konnte nicht angelegt werden');
    },
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<UserDraft> }) => updateUser(id, input),
    onSuccess: () => {
      setFeedback('Gespeichert');
      onSaved();
    },
    onError: (error) => {
      setFeedback(error instanceof Error ? `Fehler: ${error.message}` : 'Fehler: User konnte nicht gespeichert werden');
    },
  });

  return (
    <section className="space-y-5">
      <section className="rounded-md border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-sm font-semibold">User anlegen</h2>
          {feedback ? (
            <span className={`text-sm ${feedback.startsWith('Fehler:') ? 'text-red-600' : 'text-[#117f8a]'}`}>
              {feedback}
            </span>
          ) : null}
        </div>
        <div className="grid gap-4 p-5 xl:grid-cols-[1fr_1fr_220px]">
          <Field label="E-Mail">
            <input
              value={draft.email}
              onChange={(event) => {
                setFeedback(null);
                setDraft({ ...draft, email: event.target.value });
              }}
              className="w-full rounded-md border border-slate-300 px-3 py-2"
            />
          </Field>
          <Field label="Name">
            <input
              value={draft.displayName ?? ''}
              onChange={(event) => {
                setFeedback(null);
                setDraft({ ...draft, displayName: event.target.value });
              }}
              className="w-full rounded-md border border-slate-300 px-3 py-2"
            />
          </Field>
          <Field label="Rolle">
            <select
              value={draft.role}
              onChange={(event) => {
                setFeedback(null);
                setDraft({ ...draft, role: event.target.value as 'viewer' | 'admin' });
              }}
              className="w-full rounded-md border border-slate-300 px-3 py-2"
            >
              <option value="viewer">Viewer</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <Field label="Viewer Group">
            <select
              value={draft.viewerGroupId ?? ''}
              onChange={(event) => {
                setFeedback(null);
                setDraft({ ...draft, viewerGroupId: event.target.value || null });
              }}
              className="w-full rounded-md border border-slate-300 px-3 py-2"
            >
              <option value="">Keine</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </Field>
          {keycloakProvisioningEnabled ? (
            <Field label="Startpasswort">
              <input
                value={draft.password ?? ''}
                onChange={(event) => {
                  setFeedback(null);
                  setDraft({ ...draft, password: event.target.value });
                }}
                type="password"
                className="w-full rounded-md border border-slate-300 px-3 py-2"
              />
            </Field>
          ) : (
            <Field label="OIDC Subject">
              <input
                value={draft.oidcSubject ?? ''}
                onChange={(event) => {
                  setFeedback(null);
                  setDraft({ ...draft, oidcSubject: event.target.value });
                }}
                className="w-full rounded-md border border-slate-300 px-3 py-2"
              />
            </Field>
          )}
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => createMutation.mutate(draft)}
              disabled={createMutation.isPending || !draft.email}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#117f8a] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0c6871] disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              <Plus size={16} aria-hidden="true" />
              Anlegen
            </button>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-md border border-slate-200 bg-white">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">E-Mail</th>
              <th className="px-4 py-3">Viewer Group</th>
              <th className="px-4 py-3">Rolle</th>
              <th className="px-4 py-3">Status</th>
              <th className="w-14 px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <EditableUserRow
                key={user.id}
                user={user}
                groups={groups}
                onSave={(input) => updateMutation.mutate({ id: user.id, input })}
              />
            ))}
          </tbody>
        </table>
      </section>
    </section>
  );
}

function ChannelMappingsPanel({
  mappings,
  isLoading,
  onSaved,
}: {
  mappings: ChannelMapping[];
  isLoading: boolean;
  onSaved: () => void;
}) {
  const [query, setQuery] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: saveChannelMapping,
    onSuccess: () => {
      setFeedback('Gespeichert');
      onSaved();
    },
    onError: (error) => {
      setFeedback(error instanceof Error ? `Fehler: ${error.message}` : 'Fehler: Mapping konnte nicht gespeichert werden');
    },
  });

  const filteredMappings = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return mappings;
    return mappings.filter((mapping) =>
      [mapping.raw_channel, mapping.group_name, mapping.source_name, mapping.channel_type]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalized)),
    );
  }, [mappings, query]);

  return (
    <section className="overflow-hidden rounded-md border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
        <h2 className="text-sm font-semibold">Channel Mapping</h2>
        <div className="flex flex-wrap items-center gap-3">
          {feedback ? (
            <span className={`text-sm ${feedback.startsWith('Fehler:') ? 'text-red-600' : 'text-[#117f8a]'}`}>
              {feedback}
            </span>
          ) : null}
          <label className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 text-slate-400" size={17} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Suchen"
              className="w-56 rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm"
            />
          </label>
        </div>
      </div>

      <div className="overflow-auto">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Weclapp Channel</th>
              <th className="px-4 py-3">Dashboard-Name</th>
              <th className="px-4 py-3">Quelle</th>
              <th className="px-4 py-3">Bestellungen</th>
              <th className="px-4 py-3">Aktiv</th>
              <th className="px-4 py-3">Ausblenden</th>
              <th className="w-14 px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td className="px-4 py-4 text-slate-500" colSpan={7}>
                  Lade...
                </td>
              </tr>
            ) : null}
            {!isLoading && filteredMappings.length === 0 ? (
              <tr>
                <td className="px-4 py-4 text-slate-500" colSpan={7}>
                  Keine Channels gefunden
                </td>
              </tr>
            ) : null}
            {filteredMappings.map((mapping) => (
              <EditableChannelRow
                key={mapping.raw_channel}
                mapping={mapping}
                isPending={mutation.isPending}
                onSave={(input) => {
                  setFeedback(null);
                  mutation.mutate(input);
                }}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function EditableChannelRow({
  mapping,
  isPending,
  onSave,
}: {
  mapping: ChannelMapping;
  isPending: boolean;
  onSave: (input: ChannelMappingDraft) => void;
}) {
  const [groupName, setGroupName] = useState(mapping.group_name ?? '');
  const [active, setActive] = useState(mapping.active);
  const [excludeFromDashboard, setExcludeFromDashboard] = useState(mapping.exclude_from_dashboard);

  useEffect(() => {
    setGroupName(mapping.group_name ?? '');
    setActive(mapping.active);
    setExcludeFromDashboard(mapping.exclude_from_dashboard);
  }, [mapping]);

  return (
    <tr className="border-t border-slate-100">
      <td className="px-4 py-3">
        <span className="font-mono text-xs">{mapping.raw_channel}</span>
        {mapping.channel_type ? <span className="ml-2 text-xs text-slate-500">{mapping.channel_type}</span> : null}
      </td>
      <td className="px-4 py-3">
        <input
          value={groupName}
          onChange={(event) => setGroupName(event.target.value)}
          className="w-full rounded-md border border-slate-300 px-3 py-2"
        />
      </td>
      <td className="px-4 py-3 text-slate-600">{mapping.source_name ?? '-'}</td>
      <td className="px-4 py-3 tabular-nums text-slate-600">
        {mapping.order_count == null ? '-' : mapping.order_count.toLocaleString('de-DE')}
      </td>
      <td className="px-4 py-3">
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} />
          Aktiv
        </label>
      </td>
      <td className="px-4 py-3">
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={excludeFromDashboard}
            onChange={(event) => setExcludeFromDashboard(event.target.checked)}
          />
          Ausblenden
        </label>
      </td>
      <td className="px-4 py-3">
        <button
          type="button"
          title="Speichern"
          onClick={() =>
            onSave({
              id: mapping.id,
              rawChannel: mapping.raw_channel,
              groupName,
              active,
              excludeFromDashboard,
            })
          }
          disabled={isPending || !mapping.raw_channel || !groupName.trim()}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-300"
        >
          <Save size={15} aria-hidden="true" />
        </button>
      </td>
    </tr>
  );
}

function EditableUserRow({
  user,
  groups,
  onSave,
}: {
  user: PortalUser;
  groups: ViewerGroup[];
  onSave: (input: Partial<UserDraft>) => void;
}) {
  const [viewerGroupId, setViewerGroupId] = useState(user.viewer_group_id ?? '');
  const [role, setRole] = useState(user.role);
  const [disabled, setDisabled] = useState(user.disabled);

  return (
    <tr className="border-t border-slate-100">
      <td className="px-4 py-3">
        <span className="font-medium">{user.email}</span>
        <span className="block text-xs text-slate-500">{user.display_name}</span>
      </td>
      <td className="px-4 py-3">
        <select
          value={viewerGroupId}
          onChange={(event) => setViewerGroupId(event.target.value)}
          className="w-full rounded-md border border-slate-300 px-2 py-1.5"
        >
          <option value="">Keine</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
      </td>
      <td className="px-4 py-3">
        <select
          value={role}
          onChange={(event) => setRole(event.target.value as 'viewer' | 'admin')}
          className="rounded-md border border-slate-300 px-2 py-1.5"
        >
          <option value="viewer">Viewer</option>
          <option value="admin">Admin</option>
        </select>
      </td>
      <td className="px-4 py-3">
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={!disabled} onChange={(event) => setDisabled(!event.target.checked)} />
          Aktiv
        </label>
      </td>
      <td className="px-4 py-3">
        <button
          type="button"
          title="Speichern"
          onClick={() =>
            onSave({
              viewerGroupId: viewerGroupId || null,
              role,
              disabled,
            })
          }
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
        >
          <Check size={16} aria-hidden="true" />
        </button>
      </td>
    </tr>
  );
}

function TabButton({
  active,
  icon,
  onClick,
  children,
}: {
  active: boolean;
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${
        active ? 'bg-white text-[#0c6871] shadow-sm ring-1 ring-slate-200' : 'text-slate-600 hover:bg-white'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function toGroupDraft(group?: ViewerGroup): ViewerGroupDraft {
  if (!group) return emptyGroup;
  return {
    id: group.id,
    code: group.code,
    name: group.name,
    description: group.description ?? '',
    logoUrl: group.logo_url ?? '',
    showRevenue: group.show_revenue,
    active: group.active,
      scopeRules:
      group.scopeRules?.map((rule) => {
        const draftRule: ScopeRule = {
          scope_type: rule.scope_type,
          scope_value: rule.scope_value,
          include_descendants: rule.include_descendants,
          active: rule.active,
        };
        if (rule.id) draftRule.id = rule.id;
        return draftRule;
      }) ?? [],
    locale: group.settings?.locale === 'en-US' ? 'en-US' : 'de-DE',
  };
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function isViewerGroupCodeValid(value: string): boolean {
  return /^[a-z][a-z0-9-]{1,62}[a-z0-9]$/.test(value);
}
